import { Button, Divider, Dropdown, Menu, message, Modal } from 'antd';
import {
  HourglassIcon,
  PhoneCreateTicketIcon,
  PhoneFailedIcon,
  PhoneOnCallIcon,
  PhonePauseIcon,
  PhoneReadyIcon,
  PhoneRingingIcon
} from 'assets';
import { MissedCallModal, WaitingCallsDetail } from 'components';
import { useOmniCallContext } from 'contexts';
import { useProfile } from 'hooks'; // Remove useAgentStatusManager import
import { useGetWatingCalls } from 'hooks/omni_channel/useGetWaitingCalls';
import { useEffect, useRef, useState } from 'react';
import { onGetWaitingCallsEvent, onReceiveCallCdrEvent, onReceiveCallHangUpEvent, useLazyGetAgentStatusQuery, useLazyGetReasonsOptionsQuery } from 'services';
import { twMerge } from 'tailwind-merge';
import { AGENT_STATUS } from 'utils';
import PauseReasonModal from './PauseReasonModal';
import { customerLookupMessages } from 'messages/customer-lookup.message';

function CustomerLookupWaitingCall() {
  const { profile } = useProfile();

  // Reference for UI elements
  const waitingCallsRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // State to track if the query has been initialized
  const [isQueryInitialized, setIsQueryInitialized] = useState(false);

  //TO DO: uncomment nếu muốn lấy waitingcall
  // Get waiting calls
  // const { waitingCalls, refetch, count: countWaitingCalls, isSuccess } = useGetWatingCalls();

  // // Update isQueryInitialized when the query succeeds
  // useEffect(() => {
  //   if (isSuccess && !isQueryInitialized) {
  //     setIsQueryInitialized(true);
  //   }
  // }, [isSuccess, isQueryInitialized]);

  // Status and UI state
  const [currentPhoneColor, setCurrentPhoneColor] = useState(<PhoneFailedIcon height={20} width={20} />);
  const [showWaitingCallsDetail, setShowWaitingCallsDetail] = useState(false);

  // Get shared context directly, instead of using useAgentStatusManager
  const {
    agentStatus,
    getAgentStatus,
    updateAgentStatusReady,
    updateAgentStatusPause,
    updateAgentStatusCreatingTicket,
    toggleAgentStatus,
    isTogglingStatus,
    showMissedCallModal,
    missedCallData,
    handleMissedCallModalClose
  } = useOmniCallContext();

  const [fetchAgentStatus, { data: currentFetchAgentStatus }] = useLazyGetAgentStatusQuery();
  const [fetchReasons] = useLazyGetReasonsOptionsQuery();
  const isAgentCreatingTicket = localStorage.getItem('isAgentCreatingTicket');

  // Toggle waiting calls detail display
  const toggleWaitingCallsDetail = (event: React.MouseEvent) => {
    event.stopPropagation();
    setTimeout(() => {
      setShowWaitingCallsDetail((prev) => !prev);
    }, 0);
  };

  // Handle click outside to close details panel
  const handleClickOutside = (event: MouseEvent) => {
    if (buttonRef.current && buttonRef.current.contains(event.target as Node)) {
      return;
    }
    if (waitingCallsRef.current && !waitingCallsRef.current.contains(event.target as Node) && showWaitingCallsDetail) {
      setShowWaitingCallsDetail(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside, true);
    };
  }, [showWaitingCallsDetail]);

  // Add a ref to track the last time we called the API
  const lastApiCallTime = useRef<number>(0);

  // Create a safer version of handleFetchStatus that won't call too frequently
  const safeHandleFetchStatus = () => {
    if (profile?.extensionId) {
      const now = Date.now();
      const timeSinceLastCall = now - lastApiCallTime.current;

      if (timeSinceLastCall < 5000) { // 5 second protection

        return;
      }


      lastApiCallTime.current = now;
      fetchAgentStatus({ extension: profile.extensionId });
    }
  };

  // Update phone icon based on agent status - Replace the current fetch effect with this
  useEffect(() => {
    // Only fetch status once on component mount

    safeHandleFetchStatus();

    // Set up a polling interval that's offset from the other component
    // const intervalId = setInterval(() => {
    //   
    //   safeHandleFetchStatus();
    // }, 20000); // Different interval from the phone component

    // return () => {
    //   clearInterval(intervalId);
    // };
  }, [profile?.extensionId]); // Only depend on extension ID

  // Add a ref to track previous state to prevent unnecessary updates
  const previousAgentState = useRef<string | undefined>(undefined);

  // Update phone icon based on agent status
  useEffect(() => {
    // This effect should ONLY SET THE ICON, not trigger other state changes
    // that could cause re-renders

    // Create a stable reference to the current status for comparison
    // to avoid object identity issues
    const currentAgentState = getAgentStatus?.agentState;

    // Skip if status didn't actually change (prevents unnecessary re-renders)
    if (currentAgentState === previousAgentState.current) {
      return;
    }

    // Update the previous state reference
    previousAgentState.current = currentAgentState;

    // Only update the icon based on status - no API calls here!

    switch (agentStatus) {
      case AGENT_STATUS.LOGGEDOFF:
      case AGENT_STATUS.UNKNOWN:
      case AGENT_STATUS.FAILED_LOGIN:
        setCurrentPhoneColor(<PhoneFailedIcon height={20} width={20} />);
        break;
      case AGENT_STATUS.RINGING:
        setCurrentPhoneColor(<PhoneRingingIcon height={20} width={20} />);
        break;
      case AGENT_STATUS.ONCALL:
        setCurrentPhoneColor(<PhoneOnCallIcon height={20} width={20} />);
        break;
      case AGENT_STATUS.CREATING_TICKET:
        setCurrentPhoneColor(<PhoneCreateTicketIcon height={20} width={20} />);
        break;
      case AGENT_STATUS.AGENT_UNPAUSED:
        setCurrentPhoneColor(<PhoneReadyIcon height={20} width={20} />);
        break;
      case AGENT_STATUS.AGENT_PAUSED:
        setCurrentPhoneColor(<PhonePauseIcon height={20} width={20} />);
        break;
      case AGENT_STATUS.IDLE:
        if (isAgentCreatingTicket && parseInt(isAgentCreatingTicket)) {
          setCurrentPhoneColor(<PhoneCreateTicketIcon height={20} width={20} />);
          return;
        }
        if (getAgentStatus?.agentState === AGENT_STATUS.AGENT_UNPAUSED) {
          setCurrentPhoneColor(<PhoneReadyIcon height={20} width={20} />);
        } else {
          setCurrentPhoneColor(<PhonePauseIcon height={20} width={20} />);
        }
        break;
      default:
        setCurrentPhoneColor(<PhoneFailedIcon height={20} width={20} />);
        break;
    }
  }, [agentStatus, getAgentStatus?.agentState]); // Keep these dependencies minimal

  //TODO : Uncomment if you want to use the refetch function waiting calls
  // Safe refetch function
  // const safeRefetch = () => {
  //   if (isQueryInitialized) {
  //     try {
  //       refetch();
  //     } catch (error) {
  //       console.error('Error refetching waiting calls:', error);
  //     }
  //   } else {

  //   }
  // };

  // Add state for PauseReasonModal
  const [showPauseReasonModal, setShowPauseReasonModal] = useState(false);
  const [selectedPauseReason, setSelectedPauseReason] = useState<number>();

  // Handle status change from dropdown - modify to show confirmation when changing from CREATING_TICKET
  const handleStatusChange = async (status: string) => {
    if (!profile?.extensionId || !profile?.userId) {
      message.error('Không thể thay đổi trạng thái, thông tin người dùng không hợp lệ');
      return;
    }

    // Check if current status is CREATING_TICKET and show confirmation
    if (getAgentStatus?.agentState === AGENT_STATUS.CREATING_TICKET) {
      const isReady = status === AGENT_STATUS.AGENT_UNPAUSED;

      Modal.confirm({
        title: 'Xác nhận chuyển trạng thái',
        content: isReady
          ? 'Chuyển sang Sẵn sàng sẽ xóa hết thông tin đang tạo phiếu'
          : 'Chuyển sang Bận sẽ xóa hết thông tin đang tạo phiếu',
        okText: 'Xác nhận',
        cancelText: 'Hủy',
        onOk: () => {
          // Execute status change after confirmation
          if (isReady) {
            executeStatusChange(status);
          } else {
            setShowPauseReasonModal(true);
          }
        }
      });
      return;
    }

    // Otherwise proceed normally
    await executeStatusChange(status);
  };

  // New function to execute status change after confirmation
  const executeStatusChange = async (status: string) => {
    try {
      switch (status) {
        case AGENT_STATUS.AGENT_UNPAUSED:
          await toggleAgentStatus(AGENT_STATUS.AGENT_UNPAUSED);
          break;
        case AGENT_STATUS.AGENT_PAUSED:
          // Show the pause reason modal instead of immediately toggling status
          setShowPauseReasonModal(true);
          return; // Return early to prevent the success message
        case AGENT_STATUS.CREATING_TICKET:
          updateAgentStatusCreatingTicket();
          break;
      }

      message.success(`Đã chuyển sang trạng thái: ${getStatusLabel(status)}`);
    } catch (error) {
      console.error('Error changing agent status:', error);
      message.error('Không thể thay đổi trạng thái');
    }
  };

  // Helper to get status label
  const getStatusLabel = (status: string) => {
    switch (status) {
      case AGENT_STATUS.AGENT_UNPAUSED: return 'Sẵn sàng';
      case AGENT_STATUS.AGENT_PAUSED: return 'Tạm dừng';
      case AGENT_STATUS.CREATING_TICKET: return 'Đang tạo phiếu';
      default: return 'Không xác định';
    }
  };

  // Define dropdown menu items
  const statusMenuItems = [
    {
      key: AGENT_STATUS.AGENT_UNPAUSED,
      label: customerLookupMessages.readyV2,
      icon: (
        <div className={getAgentStatus?.agentState === AGENT_STATUS.AGENT_PAUSED ? '' : 'opacity-50 grayscale'}>
          <PhoneReadyIcon height={16} width={16} />
        </div>
      ),
      disabled: getAgentStatus?.agentState === AGENT_STATUS.AGENT_UNPAUSED
    },
    {
      key: AGENT_STATUS.AGENT_PAUSED,
      label: customerLookupMessages.busyV2,
      icon: (
        <div className={getAgentStatus?.agentState === AGENT_STATUS.AGENT_UNPAUSED ? '' : 'opacity-50 grayscale'}>
          <PhonePauseIcon height={16} width={16} />
        </div>
      ),
      disabled: getAgentStatus?.agentState === AGENT_STATUS.AGENT_PAUSED
    },
  ];

  // Add event handling for waiting calls with circuit breaker
  // useEffect(() => {
  //   // Setup event handlers for polling
  //   const handleWaitingCallsEvent = () => {
  //     if (isQueryInitialized) {

  //       try {
  //         refetch();
  //       } catch (error) {
  //         console.error('Error refetching waiting calls:', error);
  //       }
  //     }
  //   };

  //   // Attach listeners ONCE
  //   onGetWaitingCallsEvent(handleWaitingCallsEvent);
  //   //onReceiveCallHangUpEvent(handleWaitingCallsEvent);

  //   onReceiveCallCdrEvent(handleWaitingCallsEvent);

  //   // Clean up listeners on unmount
  //   return () => {
  //     // Ideally, clean up event listeners if your event system supports it
  //   };
  // }, [isQueryInitialized]); // Only run when query initialization state changes

  return (
    <>
      {showWaitingCallsDetail && (
        <div
          ref={waitingCallsRef}
          className='absolute right-10 top-20 z-50 rounded-[10px] bg-white'
          style={{ boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.3), 0 8px 10px -6px rgba(0, 0, 0, 0.2)' }}
        >
          {/* <WaitingCallsDetail waitingCalls={waitingCalls} /> */}
        </div>
      )}
      <Divider type='vertical' className='mx-3 h-10 2xl:mx-5' />
      <div className='flex h-10 cursor-pointer items-center overflow-hidden rounded-full border border-defaultBorderColor'>
        <Dropdown
          menu={{
            items: statusMenuItems,
            onClick: ({ key }) => handleStatusChange(key)
          }}
          trigger={['click']}
          disabled={isTogglingStatus || [AGENT_STATUS.ONCALL, AGENT_STATUS.RINGING, AGENT_STATUS.FAILED_LOGIN].includes(getAgentStatus?.agentState as AGENT_STATUS)}
        >
          <Button
            ref={buttonRef}
            icon={currentPhoneColor}
            type='text'
            className={twMerge(
              'h-10 w-13 rounded-none transition-colors duration-200'
            )}
            loading={isTogglingStatus}
          />
        </Dropdown>
        <Divider type='vertical' className='mx-0 h-6' />
        <Button
          icon={<HourglassIcon height={20} width={20} />}
          type='text'
          className={twMerge('h-10 w-[60px] rounded-none text-sm font-normal', showWaitingCallsDetail ? 'bg-[#EBEBED]' : '')}
          onClick={toggleWaitingCallsDetail}
        >
          {/* {countWaitingCalls} */}
        </Button>
        <Divider type='vertical' className='mx-0 h-6' />
      </div>

      {/* PauseReasonModal component */}
      <PauseReasonModal
        isTogglingStatus={isTogglingStatus}
        toggleAgentStatus={(action, reasonId) => toggleAgentStatus(action, reasonId)}
        showPauseReasonModal={showPauseReasonModal}
        setShowPauseReasonModal={setShowPauseReasonModal}
        setSelectedPauseReason={setSelectedPauseReason}
        selectedPauseReason={selectedPauseReason}
      />
      <MissedCallModal
        open={showMissedCallModal}
        onClose={handleMissedCallModalClose}
        customerNumber={missedCallData.customerNumber}
        callSource={missedCallData.callSource}
        callTime={missedCallData.callTime}
      />

    </>
  );
}

export default CustomerLookupWaitingCall;
